import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPrice } from "../../lib/api";
import { TrendingUp, Package, ShoppingBag, Users, Mail } from "lucide-react";

const STATUS_COLORS = {
  paid: "text-blue-300", packed: "text-yellow-300", shipped: "text-purple-300",
  out_for_delivery: "text-pink-300", delivered: "text-emerald-300", cancelled: "text-red-300",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setStats(null));
  }, []);

  if (!stats) return <div className="text-white/60">Loading…</div>;

  return (
    <div>
      <h1 className="font-display font-extrabold text-4xl tracking-tighter text-white">Dashboard</h1>
      <p className="mt-1 text-sm text-white/50">A quick read of the vault.</p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Revenue" value={formatPrice(stats.revenue_cents)} icon={TrendingUp} />
        <Stat label="Orders" value={stats.total_orders} icon={ShoppingBag} />
        <Stat label="Products" value={stats.total_products} icon={Package} />
        <Stat label="Customers" value={stats.total_users} icon={Users} />
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-elev rounded-xl p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-white">Recent orders</h2>
            <Link to="/admin/orders" className="text-sm text-[#B026FF] hover:underline">View all</Link>
          </div>
          <ul className="mt-5 divide-y divide-white/5">
            {stats.recent_orders.map((o) => (
              <li key={o.id} className="py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-white/50">{o.order_number}</div>
                  <div className="text-sm text-white truncate">{o.name} · {o.email}</div>
                </div>
                <div className={`text-xs uppercase tracking-wider font-bold ${STATUS_COLORS[o.status] || "text-white/60"}`}>
                  {o.status}
                </div>
                <div className="font-mono text-white">{formatPrice(o.total_cents)}</div>
                <Link to={`/admin/orders/${o.id}`} className="text-xs text-[#B026FF] hover:underline">Open →</Link>
              </li>
            ))}
            {stats.recent_orders.length === 0 && <li className="py-6 text-white/50 text-sm">No orders yet.</li>}
          </ul>
        </div>

        <div className="card-elev rounded-xl p-6">
          <h2 className="font-display text-xl text-white">Status breakdown</h2>
          <ul className="mt-5 space-y-3">
            {Object.entries(stats.by_status || {}).map(([s, c]) => (
              <li key={s} className="flex items-center justify-between text-sm">
                <span className={`uppercase tracking-wider font-bold ${STATUS_COLORS[s] || "text-white/70"}`}>{s.replace(/_/g, ' ')}</span>
                <span className="font-mono text-white">{c}</span>
              </li>
            ))}
            {Object.keys(stats.by_status || {}).length === 0 && (
              <li className="text-sm text-white/50">No orders yet.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="card-elev rounded-xl p-5">
      <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#B026FF] font-bold">
        <Icon size={14} /> {label}
      </div>
      <div className="mt-3 font-mono text-3xl text-white">{value}</div>
    </div>
  );
}
